import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import ReactJson from "react-json-view";
import * as XLSX from "xlsx";
import { downloadProposalPdf } from "./downloadProposalPdf.js";
import { sectionHasValues } from "./emptyProposal.js";
import { patchPreviewValue } from "./levelFundedTables.js";
import { mapWorkbook } from "./mapWorkbook.js";
import { parseStopLossPdf } from "./parseStopLossPdf.js";
import AsoForm from "./AsoForm.jsx";
import StopLossMarkdownField from "./StopLossMarkdownField.jsx";
import { ASO_TEMPLATES, isAsoTemplate } from "./asoModel.js";
import { packProposalPages } from "./packProposalPages.js";
import RenewalForm from "./RenewalForm.jsx";
import {
    DEFAULT_PHARMACY_COVERAGE,
    DEFAULT_STOP_LOSS_CONTRACT,
    applyDraftTextToPreview,
    proposalPreviewDocument,
    renderLevelFunded,
} from "./renderLevelFunded.js";
import { renderAso } from "./renderAso.js";
import { renderRenewal } from "./renderRenewal.js";
import {
    createAsoDraft,
    createLevelFundedDraft,
    createRenewalDraft,
    deleteProposal,
    listProposals,
    saveProposal,
} from "./proposalStore.js";
import "./ProposalPage.scss";

function toIsoDate(value) {
    const text = String(value || "").trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
    const slash = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
    if (!slash) return "";
    let year = Number(slash[3]);
    if (year < 100) year += year >= 70 ? 1900 : 2000;
    return `${year}-${slash[1].padStart(2, "0")}-${slash[2].padStart(2, "0")}`;
}

const WORKBOOK_EXTENSIONS = [".xlsx", ".xlsm", ".xls"];

function dragHasFiles(event) {
    return [...(event.dataTransfer?.types || [])].includes("Files");
}

function fileDropKind(event) {
    const file = event.dataTransfer?.files?.[0];
    const name = String(file?.name || "");
    const type = String(file?.type || [...(event.dataTransfer?.items || [])].find((item) => item.kind === "file")?.type || "");
    if (/\.pdf$/i.test(name) || type === "application/pdf" || /\/pdf$/i.test(type)) return "pdf";
    if (/\.xlsx?$|\.xlsm$/i.test(name) || /sheet|excel|spreadsheet/i.test(type)) return "workbook";
    return "";
}

function isWorkbookFile(file) {
    const name = String(file?.name || "").toLowerCase();
    return WORKBOOK_EXTENSIONS.some((extension) => name.endsWith(extension));
}

function pickRatesVariant(data, current) {
    const has = (section) => sectionHasValues(data?.[section]);
    if (current === "ratesWithWriteWise" && has("ratesWithWriteWise")) return current;
    if (current === "lfRateTable" && has("lfRateTable")) return current;
    if (has("rates")) return "rates";
    if (has("ratesWithWriteWise")) return "ratesWithWriteWise";
    if (has("lfRateTable")) return "lfRateTable";
    return current || "rates";
}

function templateLabel(item) {
    if (item?.templateType === "renewal") return "Renewal";
    return ASO_TEMPLATES[item?.templateType] || "Level Funded";
}

function formatUpdated(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString();
}

const TEXT_FIELDS = new Set([
    "companyName",
    "proposalDate",
    "stopLossContractCopy",
    "pharmacyCoverageCopy",
    "effectiveDate",
    "acceptanceDeadline",
    "stopLossContingencies",
]);

const DRAFT_META_FIELDS = new Set(["stopLossFileName", "stopLossNote"]);

const ProposalPage = () => {
    const [drafts, setDrafts] = useState(() => listProposals());
    const [fileError, setFileError] = useState("");
    const [dropKind, setDropKind] = useState("");
    const [downloading, setDownloading] = useState(false);
    const [parsingPdf, setParsingPdf] = useState(false);
    const { proposalId } = useParams();
    const navigate = useNavigate();
    const frameRef = useRef(null);
    const dragDepth = useRef(0);
    const readWorkbookRef = useRef(null);
    const focusSectionRef = useRef("");
    const focusFieldRef = useRef("");
    const previewSourceRef = useRef(null);
    const [previewEpoch, setPreviewEpoch] = useState(0);
    const [sideView, setSideView] = useState("fields");
    const [sideWidth, setSideWidth] = useState(320);
    const [menuOpen, setMenuOpen] = useState(false);
    const splitRef = useRef(null);
    const newMenuRef = useRef(null);
    const draft = drafts.find((item) => item.id === proposalId) || null;

    function goToProposal(id) {
        const next = id || "";
        const current = proposalId || "";
        if (next === current) return;
        navigate(next ? `/commercial/proposal/${next}` : "/commercial/proposal");
    }

    function applyPreviewFocus(doc, fieldKey, sectionId) {
        doc.querySelectorAll(".is-aso-editing").forEach((node) => node.classList.remove("is-aso-editing"));
        if (!fieldKey) return;
        let nodes = [...doc.querySelectorAll(`[data-aso-field="${CSS.escape(fieldKey)}"]`)];
        if (!nodes.length && sectionId) {
            const section = doc.querySelector(`[data-proposal-section="${sectionId}"]`);
            const heading = section?.querySelector(":scope > h1, :scope > h2, :scope > h3, :scope > .tpl-exec-heading");
            if (heading) nodes = [heading];
        }
        nodes.forEach((node) => node.classList.add("is-aso-editing"));
    }

    function scrollPreviewTo(sectionId, fieldKey) {
        focusSectionRef.current = sectionId;
        if (fieldKey !== undefined) focusFieldRef.current = fieldKey;
        const frame = frameRef.current;
        const doc = frame?.contentDocument;
        const win = frame?.contentWindow;
        if (doc) applyPreviewFocus(doc, focusFieldRef.current, sectionId);
        const section = doc?.querySelector(`[data-proposal-section="${sectionId}"]`);
        const field = focusFieldRef.current
            ? doc?.querySelector(`[data-aso-field="${CSS.escape(focusFieldRef.current)}"]`)
            : null;
        const el = field || section?.querySelector(":scope > h1, :scope > h2, :scope > h3") || section;
        if (!el || !win) return;
        const html = doc.documentElement;
        html.style.setProperty("scroll-snap-type", "none", "important");
        const top = Math.max(0, el.getBoundingClientRect().top + win.scrollY - 8);
        win.requestAnimationFrame(() => win.scrollTo(0, top));
    }

    function blurPreviewField(fieldKey) {
        if (focusFieldRef.current !== fieldKey) return;
        focusFieldRef.current = "";
        const doc = frameRef.current?.contentDocument;
        if (doc) applyPreviewFocus(doc, "", focusSectionRef.current);
    }

    function fitPreview() {
        const frame = frameRef.current;
        const doc = frame?.contentDocument;
        const root = doc?.querySelector(".proposal-preview-root");
        const page = doc?.querySelector(".tpl-cover, .tpl-aso-cover");
        if (!frame || !root || !page) return;
        root.style.zoom = "1";
        const width = page.offsetWidth;
        const height = page.offsetHeight;
        if (!width || !height) return;
        const scale = Math.min(
            (frame.clientWidth - 16) / width,
            frame.clientHeight / height,
        );
        root.style.zoom = String(Math.max(0.25, Math.min(scale, 1.25)));
    }

    function refresh(nextId = proposalId) {
        setDrafts(listProposals());
        goToProposal(nextId);
    }

    function refreshPreviewInPlace(saved) {
        const doc = frameRef.current?.contentDocument;
        const root = doc?.querySelector(".proposal-preview-root");
        const win = doc?.defaultView;
        if (!root || !win) return false;
        const y = win.scrollY;
        const zoom = root.style.zoom;
        root.innerHTML = isAsoTemplate(saved.templateType)
            ? renderAso(saved)
            : saved.templateType === "renewal"
                ? renderRenewal(saved)
                : renderLevelFunded(saved);
        delete root.dataset.packed;
        root.classList.remove("is-compact-pages");
        packProposalPages(root);
        if (focusFieldRef.current) applyPreviewFocus(doc, focusFieldRef.current, focusSectionRef.current);
        root.style.zoom = zoom;
        win.scrollTo(0, y);
        return true;
    }

    function onJsonEdit(edit) {
        const updated = edit?.updated_src;
        if (!updated || typeof updated !== "object" || Array.isArray(updated)) return false;
        if (!draft) return false;
        if (draft.templateType === "renewal") {
            const saved = saveProposal({ ...draft, renewal: updated });
            previewSourceRef.current = saved;
            setPreviewEpoch((value) => value + 1);
            setDrafts(listProposals());
            return true;
        }
        if (isAsoTemplate(draft.templateType)) {
            const saved = saveProposal({
                ...draft,
                asoPricing: updated.pricing || draft.asoPricing,
                data: updated.workbook || draft.data,
            });
            previewSourceRef.current = saved;
            setPreviewEpoch((value) => value + 1);
            setDrafts(listProposals());
            return true;
        }
        const saved = saveProposal({ ...draft, data: updated });
        previewSourceRef.current = saved;
        const doc = frameRef.current?.contentDocument;
        const parts = [...(edit.namespace || []), edit.name].filter((part) => part != null);
        const primitive = edit.new_value == null || ["string", "number", "boolean"].includes(typeof edit.new_value);
        const patched = primitive && patchPreviewValue(doc, parts, updated);
        if (!patched) refreshPreviewInPlace(saved);
        setDrafts(listProposals());
        return true;
    }

    function onResizePointerDown(event) {
        if (event.button !== 0) return;
        event.preventDefault();
        const handle = event.currentTarget;
        handle.setPointerCapture(event.pointerId);
        const startX = event.clientX;
        const startWidth = sideWidth;
        const onMove = (moveEvent) => {
            const bounds = splitRef.current?.getBoundingClientRect().width || window.innerWidth;
            const next = startWidth + moveEvent.clientX - startX;
            const max = Math.max(280, bounds - 280);
            setSideWidth(Math.round(Math.min(max, Math.max(240, next))));
        };
        const onUp = () => {
            handle.removeEventListener("pointermove", onMove);
            handle.removeEventListener("pointerup", onUp);
            handle.removeEventListener("pointercancel", onUp);
        };
        handle.addEventListener("pointermove", onMove);
        handle.addEventListener("pointerup", onUp);
        handle.addEventListener("pointercancel", onUp);
    }

    function onResizeKeyDown(event) {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        const delta = event.key === "ArrowRight" ? 24 : -24;
        const bounds = splitRef.current?.getBoundingClientRect().width || window.innerWidth;
        const max = Math.max(280, bounds - 280);
        setSideWidth((width) => Math.round(Math.min(max, Math.max(240, width + delta))));
    }

    function update(patch, liveFields) {
        if (!draft) return;
        const saved = saveProposal({ ...draft, ...patch });
        const keys = Object.keys(patch);
        const textOnly = !liveFields && keys.every((key) => TEXT_FIELDS.has(key) || DRAFT_META_FIELDS.has(key))
            && keys.some((key) => TEXT_FIELDS.has(key));
        const doc = frameRef.current?.contentDocument;
        const asoStopLossStructure = isAsoTemplate(draft.templateType)
            && Object.hasOwn(patch, "stopLossContingencies")
            && (
                !String(patch.stopLossContingencies || "").trim()
                || !doc?.querySelector('[data-aso-field="stopLossContingencies"]')
            );
        const stopLossReflow = Object.hasOwn(patch, "stopLossContingencies");
        if (stopLossReflow && doc?.querySelector(".proposal-preview-root") && refreshPreviewInPlace(saved)) {
            setDrafts(listProposals());
            return;
        }
        if ((textOnly || liveFields) && doc?.querySelector(".proposal-preview-root") && !asoStopLossStructure) {
            applyDraftTextToPreview(doc, liveFields || patch);
            setDrafts(listProposals());
            return;
        }
        previewSourceRef.current = saved;
        setPreviewEpoch((value) => value + 1);
        refresh(saved.id);
    }

    function createDraft() {
        const saved = saveProposal(createLevelFundedDraft());
        setFileError("");
        refresh(saved.id);
    }

    function createRenewal() {
        const saved = saveProposal(createRenewalDraft());
        setFileError("");
        refresh(saved.id);
    }

    function createAso(templateType) {
        const saved = saveProposal(createAsoDraft(templateType));
        setFileError("");
        refresh(saved.id);
    }

    function removeDraft(id) {
        deleteProposal(id);
        refresh(id === proposalId ? null : proposalId);
    }

    async function readWorkbook(file) {
        if (!file || !draft) return;
        if (!isWorkbookFile(file)) {
            setFileError("Use an .xlsx, .xlsm, or .xls workbook.");
            return;
        }
        setFileError("");
        try {
            const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: true, cellStyles: true });
            const mapped = mapWorkbook(workbook);
            const proposalDate = toIsoDate(mapped.proposalDate) || mapped.proposalDate || draft.proposalDate;
            const aso = isAsoTemplate(draft.templateType);
            update({
                sourceFileName: file.name,
                mapNotes: mapped.notes,
                data: mapped.data,
                companyName: mapped.companyName || draft.companyName,
                proposalDate,
                effectiveDate: toIsoDate(mapped.effectiveDate) || draft.effectiveDate,
                includeAsoOption: draft.includeAsoOption || sectionHasValues(mapped.data.asoOption),
                includeTotalCostOfCareSavings: draft.includeTotalCostOfCareSavings || sectionHasValues(mapped.data.totalCostOfCareSaving),
                ratesTableVariant: pickRatesVariant(mapped.data, draft.ratesTableVariant),
                ...(aso ? {
                    includeIllustrativeAsoPremium: draft.includeIllustrativeAsoPremium || sectionHasValues(mapped.data.asoOption),
                    includeAsoEquivalents: draft.includeAsoEquivalents || sectionHasValues(mapped.data.rates) || sectionHasValues(mapped.data.ratesWithWriteWise) || sectionHasValues(mapped.data.lfRateTable),
                } : {}),
            });
            setSideView("json");
        } catch (error) {
            setFileError(error?.message || "Could not read that workbook.");
        }
    }

    async function readStopLossPdf(file) {
        if (!file || !draft) return;
        if (!/\.pdf$/i.test(file.name || "")) {
            setFileError("Use a PDF for stop-loss contingencies.");
            return;
        }
        setParsingPdf(true);
        setFileError("");
        try {
            const parsed = await parseStopLossPdf(await file.arrayBuffer());
            update({
                stopLossContingencies: parsed.text,
                stopLossFileName: file.name,
                stopLossNote: parsed.found
                    ? ""
                    : "No contingencies heading was found, so the full PDF text is shown.",
            });
            scrollPreviewTo("appendixStopLoss", "stopLossContingencies");
        } catch (error) {
            setFileError(error?.message || "Could not read that PDF.");
        } finally {
            setParsingPdf(false);
        }
    }

    function routeDroppedFile(file) {
        if (draft?.templateType === "renewal") return;
        if (/\.pdf$/i.test(file?.name || "")) readStopLossPdf(file);
        else readWorkbook(file);
    }

    function onStopLossPdf(event) {
        const file = event.target.files?.[0];
        event.target.value = "";
        readStopLossPdf(file);
    }

    function onWorkbook(event) {
        const file = event.target.files?.[0];
        event.target.value = "";
        readWorkbook(file);
    }

    function onWorkbookDragEnter(event) {
        if (!dragHasFiles(event)) return;
        event.preventDefault();
        dragDepth.current += 1;
        setDropKind(fileDropKind(event) || "file");
    }

    function onWorkbookDragOver(event) {
        if (!dragHasFiles(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        const kind = fileDropKind(event);
        if (kind) setDropKind(kind);
    }

    function onWorkbookDragLeave() {
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDropKind("");
    }

    function onWorkbookDrop(event) {
        if (!dragHasFiles(event)) return;
        event.preventDefault();
        dragDepth.current = 0;
        setDropKind("");
        routeDroppedFile(event.dataTransfer.files?.[0]);
    }

    readWorkbookRef.current = routeDroppedFile;

    if (draft && previewSourceRef.current?.id !== draft.id) previewSourceRef.current = draft;

    const preview = useMemo(
        () => (previewSourceRef.current ? proposalPreviewDocument(previewSourceRef.current) : ""),
        [draft?.id, previewEpoch],
    );

    useEffect(() => {
        if (!menuOpen) return undefined;
        function onPointerDown(event) {
            if (!newMenuRef.current?.contains(event.target)) setMenuOpen(false);
        }
        function onKeyDown(event) {
            if (event.key === "Escape") setMenuOpen(false);
        }
        document.addEventListener("pointerdown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("pointerdown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [menuOpen]);

    useEffect(() => {
        if (!proposalId || drafts.some((item) => item.id === proposalId)) return;
        navigate("/commercial/proposal", { replace: true });
    }, [proposalId, drafts, navigate]);

    useEffect(() => {
        const frame = frameRef.current;
        const pane = frame?.parentElement;
        if (!frame || !pane) return undefined;
        const observer = new ResizeObserver(() => fitPreview());
        observer.observe(pane);
        return () => observer.disconnect();
    }, [preview]);

    async function onDownload() {
        if (!draft || !frameRef.current) return;
        setDownloading(true);
        setFileError("");
        try {
            const name = draft.companyName?.trim() || templateLabel(draft);
            const title = draft.templateType === "renewal"
                ? `Renewal Package for ${name}.pdf`
                : isAsoTemplate(draft.templateType)
                    ? `${templateLabel(draft)} for ${name}.pdf`
                    : `Proposal for ${name}.pdf`;
            downloadProposalPdf(proposalPreviewDocument(draft), title);
        } catch (error) {
            setFileError(error?.message || "Could not create the PDF.");
        } finally {
            setDownloading(false);
            fitPreview();
        }
    }

    const proposalCategories = [
        {
            label: "Level Funded",
            items: [{ label: "Level Funded", onClick: createDraft }],
        },
        {
            label: "ASO",
            items: [
                { label: "Cigna", onClick: () => createAso("aso") },
                { label: "Centivo", onClick: () => createAso("aso-centivo") },
                { label: "Wisconsin", onClick: () => createAso("aso-wisconsin") },
            ],
        },
        {
            label: "Renewal",
            items: [{ label: "Renewal", onClick: createRenewal }],
        },
    ];

    if (!draft) {
        return (
            <div className="container commercial-proposal">
                <header className="commercial-proposal__top">
                    <div>
                        <p className="commercial-proposal__eyebrow">Commercial</p>
                        <h1>Proposal</h1>
                        <p>Level Funded, ASO, and renewal packages stay in this browser until the Proposal table is ready.</p>
                    </div>
                    <div className="commercial-proposal__new" ref={newMenuRef}>
                        <button
                            type="button"
                            className="btn edit-btn"
                            aria-haspopup="menu"
                            aria-expanded={menuOpen}
                            onClick={() => setMenuOpen((open) => !open)}
                        >
                            New proposal
                        </button>
                        {menuOpen ? (
                            <div className="commercial-proposal__menu" role="menu" aria-label="Proposal templates">
                                {proposalCategories.map((category) => (
                                    <div key={category.label} className="commercial-proposal__menu-group">
                                        <div className="commercial-proposal__menu-label">{category.label}</div>
                                        {category.items.map((item) => (
                                            <button key={item.label} type="button" role="menuitem" onClick={item.onClick}>{item.label}</button>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </header>
                {drafts.length === 0 ? (
                    <p className="commercial-proposal__empty">No proposals yet. Create a Level Funded proposal or a renewal package.</p>
                ) : (
                    <div className="table-wrapper show-full-width commercial-proposal__table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Company</th>
                                    <th>Type</th>
                                    <th>Workbook</th>
                                    <th>Updated</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {drafts.map((item) => (
                                    <tr key={item.id} onClick={() => { setFileError(""); goToProposal(item.id); }}>
                                        <td>{item.companyName?.trim() || "Untitled proposal"}</td>
                                        <td>{templateLabel(item)}</td>
                                        <td>{item.sourceFileName || "—"}</td>
                                        <td>{formatUpdated(item.updatedAt)}</td>
                                        <td>
                                            <button type="button" className="commercial-proposal__delete" onClick={(event) => { event.stopPropagation(); removeDraft(item.id); }}>Delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }

    const dateValue = /^\d{4}-\d{2}-\d{2}$/.test(draft.proposalDate) ? draft.proposalDate : "";
    const isRenewal = draft.templateType === "renewal";
    const isAso = isAsoTemplate(draft.templateType);
    const dropProps = isRenewal ? {} : {
        onDragEnter: onWorkbookDragEnter,
        onDragOver: onWorkbookDragOver,
        onDragLeave: onWorkbookDragLeave,
        onDrop: onWorkbookDrop,
    };

    return (
        <div
            className={`container commercial-proposal is-editor${dropKind && !isRenewal ? " is-dropping" : ""}`}
            {...dropProps}
        >
            <header className="commercial-proposal__top">
                <div>
                    <button type="button" className="commercial-proposal__back" onClick={() => goToProposal(null)}>Proposals</button>
                    <h1>{draft.companyName?.trim() || "Untitled proposal"}</h1>
                    <p>{isRenewal ? "Renewal package. Fill the story and the tables. Internal notes stay in this panel." : isAso ? `${templateLabel(draft)}. Edit the fees, or drop the underwriting workbook to fill plan design and the premium exhibits.` : "Level Funded. Upload the workbook and the tables fill from the proposal data."}</p>
                </div>
                <div className="commercial-proposal__actions">
                    <button type="button" className="btn edit-btn" disabled={downloading} onClick={onDownload}>
                        {downloading ? "Creating PDF…" : "Download PDF"}
                    </button>
                    <button type="button" className="commercial-proposal__delete" onClick={() => removeDraft(draft.id)}>Delete</button>
                </div>
            </header>
            <div
                ref={splitRef}
                className="commercial-proposal__split"
                style={{ "--proposal-side": `${sideWidth}px` }}
            >
                <aside className="commercial-proposal__controls">
                    <div className="commercial-proposal__view" role="tablist" aria-label="Proposal editor">
                        <button type="button" role="tab" aria-selected={sideView === "fields"} className={sideView === "fields" ? "is-on" : ""} onClick={() => setSideView("fields")}>Fields</button>
                        <button type="button" role="tab" aria-selected={sideView === "json"} className={sideView === "json" ? "is-on" : ""} onClick={() => setSideView("json")}>View JSON</button>
                    </div>
                    {sideView === "json" ? (
                        <div className="commercial-proposal__json">
                            <p>{isRenewal ? "Renewal package data. Edit a value and the preview updates." : isAso ? "ASO pricing and workbook data. Edit a value and the preview updates." : "Parsed workbook data. Edit a value and the preview updates."}</p>
                            <ReactJson
                                src={isRenewal ? (draft.renewal || {}) : isAso ? { pricing: draft.asoPricing || {}, workbook: draft.data || {} } : (draft.data || {})}
                                name={false}
                                collapsed={1}
                                collapseStringsAfterLength={80}
                                displayDataTypes={false}
                                displayObjectSize={false}
                                enableClipboard
                                quotesOnKeys={false}
                                sortKeys={false}
                                onEdit={onJsonEdit}
                                onAdd={onJsonEdit}
                                onDelete={onJsonEdit}
                            />
                        </div>
                    ) : isRenewal ? (
                        <RenewalForm
                            draft={draft}
                            onPatch={update}
                            scrollPreviewTo={scrollPreviewTo}
                            blurPreviewField={blurPreviewField}
                        />
                    ) : isAso ? (
                        <AsoForm
                            draft={draft}
                            onPatch={update}
                            scrollPreviewTo={scrollPreviewTo}
                            blurPreviewField={blurPreviewField}
                            parsingPdf={parsingPdf}
                            dropKind={dropKind}
                            fileError={fileError}
                            onStopLossPdf={onStopLossPdf}
                            onWorkbook={onWorkbook}
                        />
                    ) : (
                    <>
                    <label>
                        Company
                        <input value={draft.companyName} onFocus={() => scrollPreviewTo("cover", "companyName")} onBlur={() => blurPreviewField("companyName")} onChange={(event) => update({ companyName: event.target.value })} />
                    </label>
                    <label>
                        Proposal date
                        <input type="date" value={dateValue} onFocus={() => scrollPreviewTo("cover", "proposalDate")} onBlur={() => blurPreviewField("proposalDate")} onChange={(event) => update({ proposalDate: event.target.value })} />
                    </label>
                    <label>
                        Plan offering
                        <select value={draft.planOfferingVariant} onFocus={() => scrollPreviewTo("planOffering", "planOffering")} onBlur={() => blurPreviewField("planOffering")} onChange={(event) => update({ planOfferingVariant: event.target.value })}>
                            <option value="partnership-cigna">Partnership and Cigna</option>
                            <option value="partnership-only">Partnership only</option>
                        </select>
                    </label>
                    <label className="commercial-proposal__check">
                        <input type="checkbox" checked={Boolean(draft.includeStandardPlansDisclosure)} onFocus={() => scrollPreviewTo("planOffering", "standardPlansDisclosure")} onBlur={() => blurPreviewField("standardPlansDisclosure")} onChange={(event) => update({ includeStandardPlansDisclosure: event.target.checked })} />
                        Standard plans disclosure
                    </label>
                    <label>
                        Rates table
                        <select value={draft.ratesTableVariant} onFocus={() => scrollPreviewTo("rates", "rates")} onBlur={() => blurPreviewField("rates")} onChange={(event) => update({ ratesTableVariant: event.target.value })}>
                            <option value="rates">Rate table</option>
                            <option value="ratesWithWriteWise">Rate table with WriteWise</option>
                            <option value="lfRateTable">LF rate table</option>
                        </select>
                    </label>
                    <label>
                        Stop loss contract details
                        <textarea
                            rows={8}
                            aria-label="Stop loss contract details"
                            value={draft.stopLossContractCopy || DEFAULT_STOP_LOSS_CONTRACT}
                            onFocus={() => scrollPreviewTo("stopLossContractDetails", "stopLossContract")}
                            onBlur={() => blurPreviewField("stopLossContract")}
                            onChange={(event) => update({
                                stopLossContractCopy: event.target.value === DEFAULT_STOP_LOSS_CONTRACT ? "" : event.target.value,
                            })}
                        />
                    </label>
                    <label>
                        Pharmacy coverage
                        <textarea
                            rows={6}
                            aria-label="Pharmacy coverage"
                            value={draft.pharmacyCoverageCopy || DEFAULT_PHARMACY_COVERAGE}
                            onFocus={() => scrollPreviewTo("pharmacyCoverage", "pharmacyCoverage")}
                            onBlur={() => blurPreviewField("pharmacyCoverage")}
                            onChange={(event) => update({
                                pharmacyCoverageCopy: event.target.value === DEFAULT_PHARMACY_COVERAGE ? "" : event.target.value,
                            })}
                        />
                    </label>
                    <label className="commercial-proposal__check">
                        <input type="checkbox" checked={Boolean(draft.includeAsoOption)} onFocus={() => scrollPreviewTo(draft.includeAsoOption ? "asoOption" : "pharmacyCoverage", "asoOption")} onBlur={() => blurPreviewField("asoOption")} onChange={(event) => { update({ includeAsoOption: event.target.checked }); scrollPreviewTo(event.target.checked ? "asoOption" : "pharmacyCoverage", "asoOption"); }} />
                        ASO option
                    </label>
                    <label className="commercial-proposal__check">
                        <input type="checkbox" checked={Boolean(draft.includeTotalCostOfCareSavings)} onFocus={() => scrollPreviewTo(draft.includeTotalCostOfCareSavings ? "totalCostOfCareSavings" : "whatComesNext", "totalCostOfCareSavings")} onBlur={() => blurPreviewField("totalCostOfCareSavings")} onChange={(event) => { update({ includeTotalCostOfCareSavings: event.target.checked }); scrollPreviewTo(event.target.checked ? "totalCostOfCareSavings" : "whatComesNext", "totalCostOfCareSavings"); }} />
                        Total cost of care savings
                    </label>
                    <label className="commercial-proposal__check">
                        <input type="checkbox" checked={Boolean(draft.firmProposal)} onFocus={() => scrollPreviewTo("whatComesNext", "firmProposal")} onBlur={() => blurPreviewField("firmProposal")} onChange={(event) => update({ firmProposal: event.target.checked })} />
                        Firm proposal
                    </label>
                    <label>
                        Effective date
                        <input type="date" value={draft.effectiveDate || ""} onFocus={() => scrollPreviewTo("whatComesNext", "effectiveDate")} onBlur={() => blurPreviewField("effectiveDate")} onChange={(event) => update({ effectiveDate: event.target.value })} />
                    </label>
                    <label>
                        Acceptance deadline
                        <input type="date" value={draft.acceptanceDeadline || ""} onFocus={() => scrollPreviewTo("whatComesNext", "acceptanceDeadline")} onBlur={() => blurPreviewField("acceptanceDeadline")} onChange={(event) => update({ acceptanceDeadline: event.target.value })} />
                    </label>
                    <label className={`commercial-proposal__drop${dropKind === "pdf" ? " is-over" : ""}`}>
                        Stop loss contingencies
                        <input type="file" accept=".pdf,application/pdf" disabled={parsingPdf} onChange={onStopLossPdf} />
                        <span>{parsingPdf ? "Reading the PDF…" : "or drop a stop-loss PDF on this page"}</span>
                    </label>
                    {draft.stopLossFileName ? <p className="commercial-proposal__file">{draft.stopLossFileName}</p> : null}
                    {draft.stopLossNote ? <p className="commercial-proposal__hint">{draft.stopLossNote}</p> : null}
                    <StopLossMarkdownField
                        value={draft.stopLossContingencies || ""}
                        placeholder="Markdown. Headings, blank lines, and - bullets. They appear in the appendix."
                        onFocus={() => scrollPreviewTo("appendixStopLoss", "stopLossContingencies")}
                        onBlur={() => blurPreviewField("stopLossContingencies")}
                        onChange={(value) => update({ stopLossContingencies: value, stopLossNote: "" })}
                    />
                    <label className={`commercial-proposal__drop${dropKind === "workbook" ? " is-over" : ""}`}>
                        Workbook
                        <input type="file" accept=".xlsx,.xlsm,.xls" onChange={onWorkbook} />
                        <span>or drop an .xlsx or .xlsm on this page</span>
                    </label>
                    {draft.sourceFileName ? <p className="commercial-proposal__file">{draft.sourceFileName}</p> : null}
                    {fileError ? <p className="commercial-proposal__error">{fileError}</p> : null}
                    {draft.mapNotes?.length ? (
                        <ul className="commercial-proposal__notes">
                            {draft.mapNotes.map((note) => <li key={note}>{note}</li>)}
                        </ul>
                    ) : (
                        <p className="commercial-proposal__hint">
                            Sheets: planDesign or PDsLF, ERSavingsMatrix, RateTable, Proposal Details, LFRateTable, asoOption, 5YearProjection.
                        </p>
                    )}
                    </>
                    )}
                </aside>
                <div
                    className="commercial-proposal__resizer"
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize sidebar"
                    aria-valuemin={240}
                    aria-valuemax={720}
                    aria-valuenow={sideWidth}
                    tabIndex={0}
                    onPointerDown={onResizePointerDown}
                    onKeyDown={onResizeKeyDown}
                />
                <div className="commercial-proposal__preview">
                    {dropKind && !isRenewal ? <div className="commercial-proposal__drop-overlay">{dropKind === "pdf" ? "Drop the stop-loss PDF" : dropKind === "workbook" ? "Drop the workbook" : "Drop a workbook or a stop-loss PDF"}</div> : null}
                    <iframe
                        ref={frameRef}
                        title={isRenewal ? "Renewal package preview" : isAso ? `${templateLabel(draft)} preview` : "Level Funded proposal preview"}
                        srcDoc={preview}
                        onLoad={() => {
                            const frame = frameRef.current;
                            const doc = frame?.contentDocument;
                            const win = frame?.contentWindow;
                            const root = doc?.querySelector(".proposal-preview-root");
                            if (root) {
                                root.style.zoom = "1";
                                packProposalPages(root);
                            }
                            fitPreview();
                            doc?.documentElement.style.setProperty("scroll-snap-type", "none", "important");
                            requestAnimationFrame(() => {
                                fitPreview();
                                requestAnimationFrame(() => {
                                    if (focusSectionRef.current) scrollPreviewTo(focusSectionRef.current);
                                });
                            });
                            if (doc && !doc.__proposalDrop) {
                                doc.__proposalDrop = true;
                                doc.addEventListener("dragover", (event) => {
                                    if (!dragHasFiles(event)) return;
                                    event.preventDefault();
                                    setDropKind(fileDropKind(event) || "file");
                                });
                                doc.addEventListener("drop", (event) => {
                                    if (!dragHasFiles(event)) return;
                                    event.preventDefault();
                                    dragDepth.current = 0;
                                    setDropKind("");
                                    readWorkbookRef.current?.(event.dataTransfer.files?.[0]);
                                });
                            }
                            if (win && !win.__proposalSnap) {
                                win.__proposalSnap = true;
                                win.addEventListener("wheel", () => {
                                    win.document.documentElement.style.setProperty("scroll-snap-type", "none", "important");
                                }, { passive: true });
                                let pinning = false;
                                win.addEventListener("scrollend", () => {
                                    if (pinning || win.document.documentElement.style.scrollSnapType === "none") return;
                                    const sheets = [...win.document.querySelectorAll(".tpl-cover, .tpl-aso-cover, .tpl-toc, .tpl-print-page")];
                                    const y = win.scrollY;
                                    let target = y;
                                    let best = Infinity;
                                    for (const sheet of sheets) {
                                        const top = sheet.getBoundingClientRect().top + y;
                                        const dist = Math.abs(top - y);
                                        if (dist < best) {
                                            best = dist;
                                            target = top;
                                        }
                                    }
                                    if (best > 0.5 && best < 20) {
                                        pinning = true;
                                        win.scrollTo(0, target);
                                        win.setTimeout(() => { pinning = false; }, 40);
                                    }
                                });
                            }
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default ProposalPage;
